import React, { useState, useEffect } from 'react';
import { RotateCcw, Copy, Download, Shuffle, Wand2 } from 'lucide-react';

interface ReverseMethod {
  id: string;
  name: string;
  description: string;
  fn: (str: string) => string;
}

const ReverseString: React.FC = () => {
  const [input, setInput] = useState('');
  const [selectedMethod, setSelectedMethod] = useState('basic');
  const [result, setResult] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);

  const [copySuccess, setCopySuccess] = useState(false);

  const reverseMethods: ReverseMethod[] = [
    {
      id: 'basic',
      name: 'Basic Reverse',
      description: 'Simple character reversal',
      fn: (str) => str.split('').reverse().join('')
    },
    {
      id: 'words',
      name: 'Reverse Words',
      description: 'Reverse order of words',
      fn: (str) => str.split(' ').reverse().join(' ')
    },
    {
      id: 'sentences',
      name: 'Reverse Sentences',
      description: 'Reverse order of sentences',
      fn: (str) => str.split('.').map(s => s.trim()).filter(s => s).reverse().join('. ') + (str.endsWith('.') ? '.' : '')
    },
    {
      id: 'wordsInPlace',
      name: 'Words in Place',
      description: 'Reverse each word individually',
      fn: (str) => str.split(' ').map(word => word.split('').reverse().join('')).join(' ')
    },
    {
      id: 'alternating',
      name: 'Alternating Reverse',
      description: 'Reverse every other word',
      fn: (str) => str.split(' ').map((word, i) => i % 2 === 1 ? word.split('').reverse().join('') : word).join(' ')
    },
    {
      id: 'preserveCase',
      name: 'Preserve Case',
      description: 'Reverse while maintaining original case positions',
      fn: (str) => {
        const reversed = str.toLowerCase().split('').reverse();
        return str.split('').map((char, i) => {
          const reversedChar = reversed[i];
          return char === char.toUpperCase() && char !== char.toLowerCase() 
            ? reversedChar.toUpperCase() 
            : reversedChar;
        }).join('');
      }
    },
    {
      id: 'alphanumeric',
      name: 'Alphanumeric Only',
      description: 'Reverse only letters and numbers',
      fn: (str) => {
        const alphanumeric = str.replace(/[^a-zA-Z0-9]/g, '').split('').reverse();
        let index = 0;
        return str.split('').map(char => {
          if (/[a-zA-Z0-9]/.test(char)) {
            return alphanumeric[index++];
          }
          return char;
        }).join('');
      }
    },
    {
      id: 'fibonacci',
      name: 'Fibonacci Pattern',
      description: 'Reverse using Fibonacci sequence positions',
      fn: (str) => {
        const fib = [1, 1];
        while (fib[fib.length - 1] < str.length) {
          fib.push(fib[fib.length - 1] + fib[fib.length - 2]);
        }
        
        let result = str;
        fib.forEach(pos => {
          if (pos <= str.length) {
            const segment = result.slice(0, pos);
            result = segment.split('').reverse().join('') + result.slice(pos);
          }
        });
        return result;
      }
    }
  ];

  const performReverse = async () => {
    if (!input.trim()) return;
    
    setIsAnimating(true);
    
    // Simulate processing time for animation
    await new Promise(resolve => setTimeout(resolve, 300));
    
    const method = reverseMethods.find(m => m.id === selectedMethod);
    if (method) {
      setResult(method.fn(input));
    }
    
    setIsAnimating(false);
  };

  const copyToClipboard = async () => {
    if (result) {
      await navigator.clipboard.writeText(result);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const downloadResult = () => {
    if (result) {
      const blob = new Blob([result], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'reversed-string.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const generateRandomString = () => {
    const samples = [
      "The quick brown fox jumps over the lazy dog",
      "Hello World! How are you today?",
      "Programming is fun and challenging",
      "React TypeScript Tailwind CSS",
      "Advanced string manipulation techniques",
      "Artificial Intelligence and Machine Learning"
    ];
    setInput(samples[Math.floor(Math.random() * samples.length)]);
  };

  useEffect(() => {
    if (input) {
      performReverse();
    } else {
      setResult('');
    }
  }, [input, selectedMethod]);

  const currentMethod = reverseMethods.find(m => m.id === selectedMethod);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-2 flex items-center justify-center gap-3">
            <RotateCcw className="text-purple-600" />
            Advanced String Reverser
          </h1>
          <p className="text-gray-600">Powerful string manipulation with multiple reverse algorithms</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Input Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-lg font-semibold text-gray-700">Input Text</label>
                <button
                  onClick={generateRandomString}
                  className="flex items-center gap-2 px-3 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm"
                >
                  <Shuffle size={16} />
                  Random
                </button>
              </div>
              
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Enter your text here..."
                className="w-full h-32 p-4 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none resize-none text-gray-700 transition-colors"
              />

              <div className="text-sm text-gray-500">
                Characters: {input.length} | Words: {input.trim() ? input.trim().split(/\s+/).length : 0}
              </div>
            </div>

            {/* Output Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="text-lg font-semibold text-gray-700">Result</label>
                <div className="flex gap-2">
                  <button
                    onClick={copyToClipboard}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Copy size={16} />
                    {copySuccess ? 'Copied!' : 'Copy'}
                  </button>
                  <button
                    onClick={downloadResult}
                    disabled={!result}
                    className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Download size={16} />
                    Download
                  </button>
                </div>
              </div>

              <div className={`w-full h-32 p-4 border-2 border-gray-200 rounded-xl bg-gray-50 overflow-auto transition-all duration-300 ${
                isAnimating ? 'animate-pulse bg-purple-50 border-purple-200' : ''
              }`}>
                {isAnimating ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  </div>
                ) : (
                  <div className="text-gray-700 whitespace-pre-wrap break-words">
                    {result || 'Result will appear here...'}
                  </div>
                )}
              </div>

              <div className="text-sm text-gray-500">
                Result length: {result.length}
              </div>
            </div>
          </div>
        </div>

        {/* Method Selection */}
        <div className="bg-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center gap-3 mb-6">
            <Wand2 className="text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-800">Choose Reverse Method</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {reverseMethods.map((method) => (
              <div
                key={method.id}
                onClick={() => setSelectedMethod(method.id)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-lg ${
                  selectedMethod === method.id
                    ? 'border-purple-500 bg-purple-50 shadow-md'
                    : 'border-gray-200 bg-white hover:border-purple-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`font-semibold ${
                    selectedMethod === method.id ? 'text-purple-800' : 'text-gray-800'
                  }`}>
                    {method.name}
                  </h3>
                  {selectedMethod === method.id && (
                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                  )}
                </div>
                <p className={`text-sm ${
                  selectedMethod === method.id ? 'text-purple-600' : 'text-gray-600'
                }`}>
                  {method.description}
                </p>
              </div>
            ))}
          </div>

          {/* Live Preview for Selected Method */}
          {input && (
            <div className="mt-6 p-4 bg-gray-50 rounded-xl">
              <h3 className="text-sm font-semibold text-gray-600 mb-2">Preview with current method:</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Original:</span>
                  <div className="mt-1 p-2 bg-white rounded border text-gray-700 max-h-20 overflow-auto">
                    {input}
                  </div>
                </div>
                <div>
                  <span className="text-gray-500">Reversed:</span>
                  <div className="mt-1 p-2 bg-white rounded border text-gray-700 max-h-20 overflow-auto">
                    {result}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Method Explanations */}
        <div className="mt-6 bg-white rounded-2xl shadow-xl p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">How It Works</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reverseMethods.slice(0, 6).map((method) => (
              <div key={method.id} className="p-3 bg-gray-50 rounded-lg">
                <h4 className="font-semibold text-gray-700">{method.name}</h4>
                <p className="text-sm text-gray-600 mt-1">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReverseString;